import { Converter, getClientPortalBaseUrls, productionEnvName } from '@itm/shared-frontend/lib/utils';
import getEnvVariable from './getEnvVariable';

import {
  TargetPlatform,
  PayrollStatus,
  MembershipStatus,
  SourceFileStatus,
  PayrollValidationSeverity,
  ReportStatus,
  SelectOption,
  PartTimeType,
  BreakInServiceType,
  ReportFrequency,
  FrequencyType,
  AuditMethod,
  FileUploadType,
  PensionType,
  ReportType,
  EventCode,
  ExitReasonCode,
  WorkerExclusion,
  MessageFunctionCode,
  MembershipRequestType,
  EmployerConfigurationStatus,
  DataSyncType,
  DataSyncStatus,
  PayrollAuditAction,
  PayPeriodEmployeeDataBatchSyncStatus,
  DirectDebitStatus,
  DataSyncAction,
  PayPeriodStatus,
  TransferOutType,
} from 'src/types';

export const ENV_PREFIX = 'REACT_APP_' as const;

export const ENVIRONMENT = getEnvVariable('ENVIRONMENT');
export const TARGET_PLATFORM = getEnvVariable('TARGET_PLATFORM');
export const AUTH_API_URL = getEnvVariable('AUTH_API_URL');
export const EMPLOYER_HUB_API_URL = getEnvVariable('EMPLOYER_HUB_API_URL');
export const CLIENT_PORTAL_API_URL = getEnvVariable('CLIENT_PORTAL_API_URL');

export const isProductionEnv = ENVIRONMENT === productionEnvName;
export const isAzureTargetPlatform = TARGET_PLATFORM === TargetPlatform.AZURE;

export const { clientPortalDomain, clientPortalLoginUrl, clientPortalDashboardUrl } =
  getClientPortalBaseUrls(ENVIRONMENT);

export const MAX_DOWNLOAD_SIZE_IN_BYTES = 52428800; // 50 MB
export const MAX_DOWNLOAD_SIZE_IN_MEGABYTES = Math.ceil(MAX_DOWNLOAD_SIZE_IN_BYTES / 1024 / 1024);

export const BANK_ACCOUNT_NUMBER_LENGTH = 8;
export const SORT_CODE_LENGTH = 6;

export const LEWIS_COMPANY_NAME = 'The Lewis Workplace Pension Trust';

export const sourceFileStatusLabelOverrides: Partial<Record<SourceFileStatus, string>> = {
  [SourceFileStatus.CompleteNoPayrollData]: 'Complete - No payroll data',
};

export const getSourceFileStatusLabel = (status: string | null | undefined): string => {
  if (!status) return '';
  return sourceFileStatusLabelOverrides[status as SourceFileStatus] ?? Converter.stripUpperToCapitalizedAll(status);
};

export const uploadStatusOptions: SelectOption[] = Converter.enumToSelectOptions(SourceFileStatus).map((option) => ({
  ...option,
  label: sourceFileStatusLabelOverrides[option.value as SourceFileStatus] ?? option.label,
}));
export const reportStatusOptions: SelectOption[] = Converter.enumToSelectOptions(ReportStatus);
export const reportTypeOptions: SelectOption[] = Converter.enumToSelectOptions(ReportType);
export const reportFrequencyOptions: SelectOption[] = Converter.enumToSelectOptions(ReportFrequency);
export const payrollStatusOptions: SelectOption[] = Converter.enumToSelectOptions(PayrollStatus);
export const membershipStatusOptions: SelectOption[] = [...Converter.enumToSelectOptions(MembershipStatus)];
export const payrollValidationSeverityOptions: SelectOption[] =
  Converter.enumToSelectOptions(PayrollValidationSeverity);
export const partTimeTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PartTimeType);
export const breakInServiceTypeOptions: SelectOption[] = Converter.enumToSelectOptions(BreakInServiceType);
export const membershipRequestTypeOptions: SelectOption[] = Converter.enumToSelectOptions(MembershipRequestType);
export const configurationStatusOptions: SelectOption[] = Converter.enumToSelectOptions(EmployerConfigurationStatus);
export const dataSyncTypeOptions: SelectOption[] = Converter.enumToSelectOptions(DataSyncType);
export const dataSyncStatusOptions: SelectOption[] = Converter.enumToSelectOptions(DataSyncStatus);
export const dataSyncActionOptions: SelectOption[] = Converter.enumToSelectOptions(DataSyncAction);
export const payPeriodEmployeeDataBatchSyncStatusOptions: SelectOption[] = Converter.enumToSelectOptions(
  PayPeriodEmployeeDataBatchSyncStatus,
);
export const directDebitStatusOptions: SelectOption[] = Converter.enumToSelectOptions(DirectDebitStatus);

export const payrollAuditActionOptions: SelectOption[] = Converter.enumToSelectOptions(PayrollAuditAction).map(
  ({ value, label }) => ({ value, label: value === PayrollAuditAction.GrantedAccess ? 'Granted access' : label }),
);

export const frequencyOptions: SelectOption[] = [
  { label: 'Monthly', value: FrequencyType.Monthly },
  { label: 'Weekly', value: FrequencyType.Weekly },
  { label: 'Fortnightly', value: FrequencyType.Fortnightly },
  { label: 'FourWeekly/Lunar', value: FrequencyType.FourWeekly },
  { label: 'Quarterly', value: FrequencyType.Quarterly },
  { label: 'BiAnnually', value: FrequencyType.BiAnnually },
  { label: 'Annually', value: FrequencyType.Annually },
];

export const auditMethodOptions: SelectOption[] = [
  { label: 'Bulk Upload', value: AuditMethod.BulkUpload },
  { label: 'Manual - Salary details', value: AuditMethod.ManualSalaryDetails },
  { label: 'Manual - Part time details', value: AuditMethod.ManualPartTimeDetails },
  { label: 'Manual - Break in service details', value: AuditMethod.ManualBreakInServiceDetails },
  { label: 'Manual - Revert leaver details', value: AuditMethod.RevertMembershipToActive },
  { label: 'Manual - Member request', value: AuditMethod.ManualEmployeeRequest },
  { label: 'Third-Party - Member request', value: AuditMethod.ThirdPartyMemberRequest },
  { label: 'Bulk Upload - Member request', value: AuditMethod.BulkUploadEmployeeRequest },
  { label: 'Manual - Add transfer out details', value: AuditMethod.ManualTransferOut },
];

export const fileUploadTypeOptions: SelectOption[] = [
  { label: 'DB Standard', value: FileUploadType.DBStandard },
  { label: 'DC Standard', value: FileUploadType.DCStandard },
  { label: 'PAPDIS1.0', value: FileUploadType.PAPDIS1_0 },
  { label: 'PAPDIS1.1', value: FileUploadType.PAPDIS1_1 },
  { label: 'ABF Template', value: FileUploadType.ABFTemplate },
];

export const pensionTypeOptions: SelectOption[] = Converter.enumToSelectOptions(PensionType);

export const transferOutTypeOptions: SelectOption[] = [
  { label: 'Payroll transfer', value: TransferOutType.PayrollTransfer },
  { label: 'Employer transfer', value: TransferOutType.EmployerTransfer },
];

export const eventCodeMapping = {
  [EventCode.NotAnEnrolmentEvent]: 'Not an enrolment event',
  [EventCode.AutomaticEnrolment]: 'Automatic enrolment',
  [EventCode.OptIn]: 'Opt-In',
  [EventCode.VoluntaryJoiner]: 'Voluntary Joiner/Joiner',
  [EventCode.ContractualEnrolment]: 'Contractual Enrolment',
  [EventCode.JoinRequest]: 'Opt in/Join request',
  [EventCode.CyclicalReEnrolment]: 'Cyclical re-enrolment',
  [EventCode.ImmediateReEnrolment]: 'Immediate re-enrolment',
};

export const exitReasonCodeMapping = {
  [ExitReasonCode.NotProvided]: 0,
  [ExitReasonCode.EmployeeLeftEmployment]: 1,
  [ExitReasonCode.CeasedActiveMembership]: 2,
  [ExitReasonCode.Death]: 3,
  [ExitReasonCode.Retirement]: 4,
  [ExitReasonCode.FlexibleRetirement]: 5,
  [ExitReasonCode.IllHealthRetirement]: 6,
  [ExitReasonCode.IntercompanyTransferOut]: 7,
  [ExitReasonCode.OptOut]: 8,
};

export const exitReasonCodeABFMapping = {
  [ExitReasonCode.NotProvided]: '',
  [ExitReasonCode.OptOut]: 'OO',
  [ExitReasonCode.EmployeeLeftEmployment]: '',
  [ExitReasonCode.CeasedActiveMembership]: 'LE',
  [ExitReasonCode.FlexibleRetirement]: 'FR',
  [ExitReasonCode.Retirement]: 'RE',
  [ExitReasonCode.IllHealthRetirement]: 'IH',
  [ExitReasonCode.Death]: 'DE',
  [ExitReasonCode.IntercompanyTransferOut]: 'TO',
};

export const messageFunctionCodeMapping = {
  [MessageFunctionCode.Enrol]: 0,
  [MessageFunctionCode.InfoOnly]: 1,
  [MessageFunctionCode.AssessmentRequest]: 2,
  [MessageFunctionCode.AssessmentResponse]: 3,
  [MessageFunctionCode.WorkerInstruction]: 4,
};

export const workerExclusionMapping = {
  [WorkerExclusion.OtherOrNotKnown]: 0,
  [WorkerExclusion.NotAWorker]: 1,
  [WorkerExclusion.NotWorkingInTheUK]: 2,
  [WorkerExclusion.WorkingInTheUK]: 3,
  [WorkerExclusion.OutsideTheAgeRange]: 4,
  [WorkerExclusion.SingleEmployeeOrDirector]: 5,
  [WorkerExclusion.CeasedActiveMembershipQPSWithInLast12months]: 6,
  [WorkerExclusion.CeasedActiveMembershipQPSMoreThan12months]: 7,
  [WorkerExclusion.WULSWithInLast12months]: 8,
  [WorkerExclusion.WULMoreThan12months]: 9,
  [WorkerExclusion.WorkerInANoticePeriod]: 10,
  [WorkerExclusion.WorkerWhoHasHMRCTaxProtection]: 11,
};

export const payPeriodStatusMapping = {
  [PayPeriodStatus.NoDataLoaded]: 'Pending',
  [PayPeriodStatus.DataLoaded]: 'Uploaded',
  [PayPeriodStatus.PreppingAdminPlatformData]: 'Preparing for submission',
  [PayPeriodStatus.FailedSubmitAdminPlatformData]: 'Data failed to submit Admin platform',
  [PayPeriodStatus.DataSubmittedAdminPlatform]: 'Submitted to Admin Platform',
  [PayPeriodStatus.AwaitingMoney]: 'Pending receipt of money (Awaiting money)',
  [PayPeriodStatus.DirectDebit]: 'Pending receipt of money (Direct debit)',
  [PayPeriodStatus.Completed]: 'Payroll completed - Money received',
  [PayPeriodStatus.Cancelled]: 'Period cancelled in admin platform',
  [PayPeriodStatus.DataSubmissionAcceptedOnAdminPlatform]: 'Data Submission Accepted on Admin Platform',
  [PayPeriodStatus.CompletedNoPayrollData]: 'Complete - No payroll data',
};

export const payPeriodStatusOptions: SelectOption[] = Object.entries(payPeriodStatusMapping).map(([value, label]) => ({
  label,
  value,
}));
